/**
 * Entry-point for the four statically-rendered /about pages.
 * The HTML content is baked into the .html files (for SEO). This script just
 * adds dynamic bits: user widget in the topbar (login / logout) and tiny
 * niceties like keeping a logged-in user's name visible.
 *
 * Each language version of about.html points to this module — Vite produces a
 * single chunk shared between all four locales.
 */
import '../styles/about.css';
import { api, ApiError } from '../data/api.js';

interface CurrentUser {
  email: string;
  displayName: string | null;
}

/**
 * Insert a small "Hello, name" + Sign-out widget next to the language switcher
 * when a session cookie is present.
 */
async function mountUserBadge(): Promise<void> {
  const nav = document.querySelector<HTMLElement>('.about-nav');
  if (!nav) return;
  let user: CurrentUser | null = null;
  try {
    const r = await api.get<{ user: CurrentUser }>('/api/me');
    user = r.user;
  } catch (err) {
    if (!(err instanceof ApiError && err.status === 401)) {
      console.warn('[about] /api/me failed', err);
    }
    return;
  }
  if (!user) return;

  const name = user.displayName ?? user.email.split('@')[0] ?? '';
  const badge = document.createElement('div');
  badge.className = 'about-user';
  badge.innerHTML = `
    <span class="about-user-name">${escape(name)}</span>
    <button type="button" class="about-user-logout">↪</button>
  `;
  nav.insertBefore(badge, nav.firstChild);

  // Minimal CSS-in-JS for the badge (kept here to avoid bloating about.css).
  const style = document.createElement('style');
  style.textContent = `
    .about-user { display:inline-flex; align-items:center; gap:6px;
      background: rgba(255,255,255,.16); padding:5px 12px; border-radius:999px;
      color:#fff; font-size:12px; font-weight:600; border:1px solid rgba(255,255,255,.22); }
    .about-user-name { letter-spacing:.02em; }
    .about-user-logout { background:transparent; border:none; color:#fff; cursor:pointer;
      font-size:14px; padding:0 0 0 2px; line-height:1; opacity:.7; }
    .about-user-logout:hover { opacity:1; }
  `;
  document.head.appendChild(style);

  badge.querySelector<HTMLButtonElement>('.about-user-logout')?.addEventListener('click', async () => {
    try {
      await api.post('/api/auth/logout', {});
    } finally {
      window.location.reload();
    }
  });
}

function escape(value: string): string {
  return value.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c),
  );
}

void mountUserBadge();
